<template>
  <div
    class="modalSmall"
    @keydown.shift.tab.exact="trapTabKey"
    @keydown.tab.exact="trapTabKey"
    @keydown.esc.exact="closeModal"
  >
    <div role="dialog" aria-modal="true" class="modalSmall--container">
      <!-- removeTppConsent -->
      <div v-if="modal.modalType === 'removeTppConsent'">
        <header class="modalSmall--header error">
          <div class="title">
            <h4>{{ t('pages.tpp.remove.modal.title') }}</h4>
          </div>

          <div class="cta">
            <button
              class="btn btn-small btn-ico btn-modal-small-close"
              :title="t('interface.close')"
              @click.prevent="closeModal"
            >
              <svg
                class="ico-error"
                aria-hidden="true"
                focusable="false"
                viewBox="0 0 512 512"
              >
                <use href="#ico-error" xlink:href="#ico-error" />
              </svg>
            </button>
          </div>
        </header>

        <div class="modalSmall--body">
          <p v-html="removeTppConsentBodyText" />
        </div>

        <!-- footer -->
        <div class="separator no-bottom-space" />

        <footer class="modalSmall--footer">
          <button
            class="btn btn-medium btn-secondary"
            @click.prevent="cancelTppConsentRemove"
          >
            {{ t('interface.cancel') }}
          </button>
          <button
            class="btn btn-medium btn-danger btn-secondary"
            @click.prevent="confirmTppConsentRemove"
          >
            {{ t('interface.confirm') }}
          </button>
        </footer>
      </div>

      <!-- eokBlock -->
      <div v-if="modal.modalType === 'eokBlock'">
        <header class="modalSmall--header error">
          <div class="title">
            <h4>{{ t('pages.authTools.st.blockSt') }}?</h4>
          </div>

          <div class="cta">
            <button
              class="btn btn-small btn-ico btn-modal-small-close"
              :title="t('interface.close')"
              @click.prevent="closeModal"
            >
              <svg
                class="ico-error"
                aria-hidden="true"
                focusable="false"
                viewBox="0 0 512 512"
              >
                <use href="#ico-error" xlink:href="#ico-error" />
              </svg>
            </button>
          </div>
        </header>

        <div class="modalSmall--body">
          <p v-html="eokBlockBodyText" />
        </div>

        <!-- footer -->
        <div class="separator no-bottom-space" />

        <footer class="modalSmall--footer">
          <button
            class="btn btn-medium btn-secondary"
            @click.prevent="closeModal"
          >
            {{ t('interface.cancel') }}
          </button>
          <button
            class="btn btn-medium btn-danger btn-secondary"
            @click="authToolBlock(st.selectedEok.id, 'DENIED')"
          >
            {{ t('interface.confirm') }}
          </button>
        </footer>
        <!-- samsung SM-A515F, SM-A515F -->
      </div>

      <!-- smsKeyBlock -->
      <div v-if="modal.modalType === 'smsKeyBlock'">
        <header class="modalSmall--header error">
          <div class="title">
            <h4>{{ t('pages.authTools.st.blockSt') }}?</h4>
          </div>

          <div class="cta">
            <button
              class="btn btn-small btn-ico btn-modal-small-close"
              :title="t('interface.close')"
              @click.prevent="closeModal"
            >
              <svg
                class="ico-error"
                aria-hidden="true"
                focusable="false"
                viewBox="0 0 512 512"
              >
                <use href="#ico-error" xlink:href="#ico-error" />
              </svg>
            </button>
          </div>
        </header>

        <div class="modalSmall--body">
          <p v-html="smsKeyBlockBodyText" />
        </div>

        <!-- footer -->
        <div class="separator no-bottom-space" />

        <footer class="modalSmall--footer">
          <button
            class="btn btn-medium btn-secondary"
            @click.prevent="closeModal"
          >
            {{ t('interface.cancel') }}
          </button>
          <button
            class="btn btn-medium btn-danger btn-secondary"
            @click="authToolBlock(st.selectedSmsKey.id, 'DENIED')"
          >
            {{ t('interface.confirm') }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import ApiCalls from '@/classes/ApiCalls';
import router from '@/router';
import global from '@/store/global';
import modal from '@/store/modal';
import modalSmall from '@/store/modalSmall';
import st from '@/store/st';
import tpp from '@/store/tpp';
import { ConsentI } from '@/support/interfaces';
import { RequestResultT } from '@/support/types';

const { t } = useI18n();
const apiCalls = new ApiCalls();

modal.modalType = modalSmall.modalType;

// GETTERS
// get text for removing tpp consent
const removeTppConsentBodyText = computed(() => {
  const selectedConsent: ConsentI[] = tpp.tpps.filter(
    (consent: ConsentI) => consent.consentId === tpp.selectedTppId,
  );
  const translation = selectedConsent[0].consentName
    ? t('pages.tpp.remove.modal.bodyText', {
        name: selectedConsent[0].consentName,
      })
    : t('pages.tpp.remove.modal.bodyTextNoName', {
        name: selectedConsent[0].consentName,
      });
  return translation;
});

// get text for block EOK body
const eokBlockBodyText = computed(() => {
  return t('pages.authTools.eok.blockModal.bodyText', {
    eokId: st.selectedEok.name,
  });
});

// get text for block EOK body
const smsKeyBlockBodyText = computed(() => {
  return t('pages.authTools.smsKey.blockModal.bodyText', {
    phoneNr: st.selectedSmsKey.name,
  });
});

// METHODS
const closeModal = (): void => {
  modalSmall.modalOpened = false;
  const pageFocusedElement = (document.activeElement ||
    document.body) as HTMLElement;
  pageFocusedElement.focus();
};

const openResultModal = (type: string) => {
  closeModal();
  modal.modalType = type;
  modal.modalOpened = true;
  global.loaderOpened = false;
};

const cancelTppConsentRemove = (): void => {
  closeModal();
};

const confirmTppConsentRemove = (): void => {
  // show loader
  global.loaderOpened = true;

  // hide confirm modal
  closeModal();

  // remove token
  apiCalls
    .deleteConsent(tpp.selectedTppId)
    .then((deleteConsentResult: RequestResultT) => {
      if (deleteConsentResult.success) {
        tpp.tpps = [];
        tpp.tppsLoaded = false;
        router.push({ name: 'tpp' });
      } else {
        global.loaderOpened = false;
        modal.modalType = 'deleteConsentFailed';
        modal.modalOpened = true;
      }
    })
    .catch(() => {
      global.loaderOpened = false;
      modal.modalType = 'deleteConsentFailed';
      modal.modalOpened = true;
    });
};

const authToolBlock = (id: string, state: string) => {
  // start loader
  global.loaderOpened = true;

  // call backend
  apiCalls
    .postAuthToolBlock(id, { state })
    .then((authBlockResponse: RequestResultT) => {
      // handle response
      if (authBlockResponse.success) {
        openResultModal('authBlockSuccess');
      } else {
        openResultModal('authBlockFailed');
      }
    })
    .catch(() => {
      modal.modalType = 'techError';
      modal.modalOpened = true;
      global.loaderOpened = false;
    })
    .then(() => {
      closeModal();
      global.loaderOpened = false;
    });
};
</script>

<style lang="scss">
@use '@/assets/scss/colors.scss';
@use '@/assets/scss/variables.scss';
@use '@/assets/scss/mixins.scss';

.modalSmall {
  position: fixed;
  z-index: 1003;
  width: 100%;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;

  padding-left: variables.$baseGridPadding;
  padding-right: variables.$baseGridPadding;

  background-color: rgba(colors.$g-primary-900, 0.3);

  &--container {
    overflow: hidden;
    width: 100%;
    max-width: mixins.calculateRem(400px);

    border-radius: variables.$border-radius;
    background-color: colors.$g-white;
  }

  &--header {
    display: grid;
    grid-template-columns: 60px 1fr 60px;
    grid-template-rows: 1fr;

    position: relative;

    background-color: colors.$eg-grey;

    @include mixins.fontSize(24px);
    @include mixins.lineHeight(28px);
    text-align: center;
    color: colors.$g-primary-900;
    font-weight: lighter;

    padding: mixins.calculateRem(10px)
      mixins.calculateRem(variables.$innerPadding);
    margin-bottom: variables.$spacingHeaderContent * 0.5;

    &.error {
      background-color: colors.$g-red-600;

      color: colors.$g-white;

      .cta {
        svg {
          fill: colors.$g-white;
        }
      }
    }

    &.success {
      background-color: colors.$g-green-500;

      .cta {
        svg {
          fill: colors.$g-white;
        }
      }
    }

    .title,
    .cta {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .title {
      grid-area: 1 / 2 / 2 / 3;

      h4 {
        margin-bottom: 0;
        padding: mixins.calculateRem(4px) 0;
      }

      @include mixins.max-breakpoint('xs') {
        grid-area: 1 / 1 / 2 / 3;
        text-align: left;
      }
    }

    .cta {
      grid-area: 1 / 3 / 2 / 4;
      justify-self: flex-end;

      svg {
        fill: colors.$g-primary-900;
      }
    }
  }

  &--body {
    padding: variables.$innerPadding;

    #changeMtokenName {
      margin-bottom: mixins.calculateRem(variables.$padding * 0.5);
    }
    p.error {
      color: colors.$g-red-600;
    }
  }

  &--footer {
    padding: variables.$innerPadding;

    display: flex;
    justify-content: center;
    align-items: center;

    button {
      &:not(:last-child) {
        margin-right: variables.$padding;
      }
    }
  }
}
</style>
